import { useEffect } from 'react'
import {
  Box, Card, CardContent, CircularProgress, Grid,
  MenuItem, Select, Typography, Alert
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useQuery } from '@tanstack/react-query'
import { portfolioApi } from '../../services/api'
import { usePortfolioStore } from '../../store/portfolioStore'

function KpiCard({
  label, value, sub, positive,
}: {
  label: string; value: string; sub?: string; positive?: boolean
}) {
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {positive !== undefined && (
            positive
              ? <TrendingUpIcon color="success" />
              : <TrendingDownIcon color="error" />
          )}
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
        </Box>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  )
}

export default function PortfolioDashboard() {
  const { portfolios, selectedPortfolioId, summary, setPortfolios, selectPortfolio, setSummary } =
    usePortfolioStore()

  // Load portfolio list
  const { data: portfolioData, isLoading: listLoading } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioApi.list().then((r) => r.data),
  })

  useEffect(() => {
    if (portfolioData?.content) {
      setPortfolios(portfolioData.content)
      if (!selectedPortfolioId && portfolioData.content.length > 0) {
        selectPortfolio(portfolioData.content[0].id)
      }
    }
  }, [portfolioData, selectedPortfolioId, setPortfolios, selectPortfolio])

  // Load KPI summary
  const { data: summaryData, isLoading: summaryLoading, error } = useQuery({
    queryKey: ['portfolio-summary', selectedPortfolioId],
    queryFn: () => portfolioApi.summary(selectedPortfolioId!).then((r) => r.data),
    enabled: !!selectedPortfolioId,
  })

  useEffect(() => {
    if (summaryData) setSummary(summaryData)
  }, [summaryData, setSummary])

  if (listLoading) return <CircularProgress />

  const fmt = (n: number, decimals = 2) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(n)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Portfolio Dashboard</Typography>
        <Select
          size="small"
          value={selectedPortfolioId ?? ''}
          onChange={(e) => selectPortfolio(e.target.value)}
          displayEmpty
          sx={{ minWidth: 200 }}
        >
          {portfolios.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </Select>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load portfolio data</Alert>}

      {summaryLoading ? (
        <CircularProgress />
      ) : summary ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Total P&L"
              value={`$${fmt(summary.totalPnl)}`}
              sub={`${fmt(summary.totalPnlPct)}%`}
              positive={summary.totalPnl >= 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Current Drawdown"
              value={`${fmt(summary.currentDrawdown)}%`}
              sub={`Max: ${fmt(summary.maxDrawdown)}%`}
              positive={summary.currentDrawdown === 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Win Rate"
              value={`${fmt(summary.winRate)}%`}
              sub={`${summary.totalTrades} total trades`}
              positive={summary.winRate >= 50}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Open Positions"
              value={`${summary.openTrades}`}
              sub="active trades"
            />
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Select a portfolio to see KPIs</Alert>
      )}
    </Box>
  )
}
